import {isNotNullOrUndefined, SetState} from '@wandb/weave/common/util/types';
import produce from 'immer';
import React, {useCallback, useMemo} from 'react';

import {ReorderFn} from '../../util/dragDrop';
import {
  ShowcasedProject,
  useEntityPageContext,
  useEntityPageUpdaterContext,
} from './context';
import {ProjectShowcaseCards} from './ProjectShowcaseCards';
import {getShowcasedProject, ProjectData} from './util';

type ProjectShowcaseSectionProps = {
  projects: ProjectData[];
};

export const ProjectShowcaseSection: React.FC<ProjectShowcaseSectionProps> =
  React.memo(({projects}) => {
    const {showcasedProjectIDs, readOnly} = useEntityPageContext();
    const {setShowcasedProjectIDs} = useEntityPageUpdaterContext();

    const showcasedProjects: ShowcasedProject[] = useMemo(() => {
      const metadataByProjectID: Map<string, ProjectData> = new Map(
        projects.map(p => [p.id, p])
      );

      return showcasedProjectIDs
        .map(id => {
          const metadata = metadataByProjectID.get(id);
          if (metadata == null) {
            return null;
          }
          return getShowcasedProject(metadata);
        })
        .filter(isNotNullOrUndefined);
    }, [showcasedProjectIDs, projects]);

    const reorderShowcasedProject = useReorderShowcasedProject({
      ids: showcasedProjectIDs,
      projects: showcasedProjects,
      setIDs: setShowcasedProjectIDs,
    });

    return (
      <ProjectShowcaseCards
        items={showcasedProjects}
        reorderItems={reorderShowcasedProject}
        readOnly={readOnly}
      />
    );
  });

type UseReorderShowcasedProjectParams = {
  ids: string[];
  projects: ShowcasedProject[];
  setIDs: SetState<string[]>;
};

// HAX: The list if projects may differ from the list of IDs because
// we don't show projects for which metadata fetching fails.
// This can happen if the user showcases a project that is subsequently deleted.
// To account for this possibility, we must map project indices to ID indices
// so the reordering works properly.
// TODO(axel): This is NASTY. You can do better.
function useReorderShowcasedProject({
  ids,
  projects,
  setIDs,
}: UseReorderShowcasedProjectParams): ReorderFn {
  const idIndexByProjectIndex: Map<number, number> = useMemo(() => {
    const projectIndexByID: Map<string, number> = new Map(
      projects.map((p, i) => [p.id, i])
    );
    const idIndexByID: Map<string, number> = new Map(
      ids.map((id, i) => [id, i])
    );

    return new Map(
      projects
        .map(p => {
          const projectIndex = projectIndexByID.get(p.id);
          const idIndex = idIndexByID.get(p.id);
          if (projectIndex == null || idIndex == null) {
            return null;
          }
          return [projectIndex, idIndex] as const;
        })
        .filter(isNotNullOrUndefined)
    );
  }, [ids, projects]);

  return useCallback(
    (fromProjectIndex: number, toProjectIndex: number) => {
      const fromIDIndex = idIndexByProjectIndex.get(fromProjectIndex);
      const toIDIndex = idIndexByProjectIndex.get(toProjectIndex);
      if (fromIDIndex == null || toIDIndex == null) {
        return;
      }
      setIDs(prev =>
        produce(prev, draft => {
          const item = draft[fromIDIndex];
          draft.splice(fromIDIndex, 1);
          draft.splice(toIDIndex, 0, item);
        })
      );
    },
    [idIndexByProjectIndex, setIDs]
  );
}
